<script setup>
// Settings page frame from the Laravel starter kit: a heading, a section
// nav on the left and the active section on the right. Each nav item is a
// link (`to`), so sections can be bookmarked and the back button works.
defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  items: { type: Array, required: true }, // [{ id, title, icon, to }]
  activeId: { type: String, default: '' },
});
</script>

<template>
  <div class="settings">
    <header class="settings__heading">
      <h2>{{ title }}</h2>
      <p v-if="description">{{ description }}</p>
    </header>

    <div class="settings__layout">
      <nav class="settings__nav" :aria-label="`${title} sections`">
        <router-link
          v-for="item in items"
          :key="item.id"
          :to="item.to"
          class="settings__nav-item"
          :class="{ 'settings__nav-item--active': item.id === activeId }"
          :aria-current="item.id === activeId ? 'page' : undefined"
        >
          <v-icon size="16">{{ item.icon }}</v-icon>
          {{ item.title }}
        </router-link>
      </nav>

      <div class="settings__content">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 4px;
}

.settings__heading h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--foreground);
}
.settings__heading p {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--muted-foreground);
}

.settings__layout {
  display: flex;
  gap: 48px;
  align-items: flex-start;
}

.settings__nav {
  flex: 0 0 192px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.settings__nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  color: var(--foreground);
  font-size: 14px;
  text-decoration: none;
}
.settings__nav-item .v-icon {
  color: var(--muted-foreground);
}
.settings__nav-item:hover {
  background: var(--accent);
}
.settings__nav-item--active {
  background: var(--accent);
  font-weight: 500;
}
.settings__nav-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}

.settings__content {
  flex: 1;
  min-width: 0;
  max-width: 640px;
}

/* Narrow screens: the nav becomes a horizontal strip above the content. */
@media (max-width: 960px) {
  .settings__layout {
    flex-direction: column;
    gap: 24px;
  }
  .settings__nav {
    flex: none;
    flex-direction: row;
    width: 100%;
    overflow-x: auto;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }
  .settings__nav-item {
    flex-shrink: 0;
  }
}
</style>
